import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

function AuditDeck({ audit }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [downloading, setDownloading] = useState(false);

  // Check for error state (parsing failed or incomplete audit)
  if (audit.error || !audit.grades) {
    return (
      <div className="w-full max-w-3xl mx-auto">
        <div className="bg-gradient-to-br from-slate-900 via-red-900/30 to-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-red-500/20 p-8">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Audit Generation Issue</h2>
          <p className="text-white mb-4">
            We encountered an issue generating your audit. This sometimes happens when the AI response doesn't format correctly.
          </p>
          <p className="text-slate-400 mb-6">
            Please try again, or try using a different input method (Screenshot is usually most reliable).
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-amber-400 text-slate-900 font-bold rounded-lg hover:bg-amber-500 transition"
          >
            Try Again
          </button>
          <details className="text-sm mt-6">
            <summary className="text-slate-500 cursor-pointer hover:text-slate-300">Technical details</summary>
            <pre className="mt-2 p-4 bg-slate-800 rounded-lg text-slate-400 overflow-x-auto text-xs max-h-40 overflow-y-auto">
              {audit.rawResponse || audit.error || 'No grades data received'}
            </pre>
          </details>
        </div>
      </div>
    );
  }

  const { grades, analysis, banner, headlines, bio, suggestedSteps, profileData, qualityCheck, calendlyUrl } = audit;
  const name = profileData?.name || 'Your Profile';

  const handleDownloadPDF = async () => {
    setDownloading(true);
    const originalSlide = currentSlide;

    // PDF dimensions (landscape)
    const pdfWidth = 1200;
    const pdfHeight = 675;

    try {
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [pdfWidth, pdfHeight],
        compress: true
      });

      // Get the slide container element
      const slideContainer = document.getElementById('slide-container');
      if (!slideContainer) {
        throw new Error('Slide container not found');
      }

      // Capture each slide
      for (let i = 0; i < slides.length; i++) {
        // Change to this slide
        setCurrentSlide(i);

        // Wait for React to render
        await new Promise(resolve => setTimeout(resolve, 200));

        // Capture with html2canvas (scale 1.5 balances quality and file size)
        const canvas = await html2canvas(slideContainer, {
          scale: 1.5,
          useCORS: true,
          backgroundColor: '#0f172a',
          logging: false
        });

        // Use JPEG with 85% quality for much smaller file size
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        if (i > 0) {
          pdf.addPage([pdfWidth, pdfHeight], 'landscape');
        }
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      }

      // Restore original slide
      setCurrentSlide(originalSlide);

      // Download PDF
      const fileName = name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
      pdf.save(`linkedin-audit-${fileName}.pdf`);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
      setCurrentSlide(originalSlide);
    }
    setDownloading(false);
  };

  const slides = [
    // Slide 0: Title
    <TitleSlide key="title" name={name} />,
    // Slide 1: Grade Overview
    <GradeSlide key="grades" grades={grades} name={name} />,
    // Slide 2: Analysis
    <AnalysisSlide key="analysis" analysis={analysis} />,
    // Slide 3: Banner
    <BannerSlide key="banner" banner={banner} grade={grades?.banner} />,
    // Slide 4: Headlines
    <HeadlineSlide key="headlines" headlines={headlines} grade={grades?.headline} />,
    // Slide 5: Bio
    <BioSlide key="bio" bio={bio} grade={grades?.bio} />,
    // Slide 6: Steps
    <StepsSlide key="steps" steps={suggestedSteps} qualityCheck={qualityCheck} />,
    // Slide 7: CTA
    <CTASlide key="cta" calendlyUrl={calendlyUrl} name={name} />
  ];

  const nextSlide = () => setCurrentSlide((prev) => Math.min(prev + 1, slides.length - 1));
  const prevSlide = () => setCurrentSlide((prev) => Math.max(prev - 1, 0));

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Slide Container */}
      <div
        id="slide-container"
        className="bg-gradient-to-br from-slate-900 via-purple-900/50 to-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-purple-500/20 min-h-[600px] relative"
      >
        {/* Radial light effect */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />
        
        {/* Slide content */}
        <div className="relative z-10 p-8 min-h-[600px]">
          {slides[currentSlide]}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-4 mt-6">
        <button 
          onClick={prevSlide} 
          disabled={currentSlide === 0}
          className="px-6 py-2 bg-slate-800 text-white rounded-lg disabled:opacity-30 hover:bg-slate-700 transition"
        >
          ← Prev
        </button>
        <div className="flex gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`w-3 h-3 rounded-full transition ${i === currentSlide ? 'bg-amber-400' : 'bg-slate-600 hover:bg-slate-500'}`}
            />
          ))}
        </div>
        <button 
          onClick={nextSlide} 
          disabled={currentSlide === slides.length - 1}
          className="px-6 py-2 bg-slate-800 text-white rounded-lg disabled:opacity-30 hover:bg-slate-700 transition"
        >
          Next →
        </button>
      </div>
      
      {/* Slide counter and download button */}
      <div className="flex items-center justify-center gap-4 mt-4">
        <div className="text-slate-400 text-sm">
          Slide {currentSlide + 1} of {slides.length}
        </div>
        <button
          onClick={handleDownloadPDF}
          disabled={downloading}
          className="px-4 py-2 bg-amber-400 text-slate-900 rounded-lg font-semibold hover:bg-amber-500 transition disabled:opacity-50 flex items-center gap-2"
        >
            {downloading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Generating PDF...
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download PDF
              </>
            )}
          </button>
      </div>
    </div>
  );
}

// Score Display Component
function ScoreCircle({ score, size = 'large', label }) {
  const getColor = (s) => {
    if (s >= 8) return { ring: 'stroke-green-400', text: 'text-green-400', bg: 'bg-green-400' };
    if (s >= 6) return { ring: 'stroke-yellow-400', text: 'text-yellow-400', bg: 'bg-yellow-400' };
    if (s >= 4) return { ring: 'stroke-orange-400', text: 'text-orange-400', bg: 'bg-orange-400' };
    return { ring: 'stroke-red-400', text: 'text-red-400', bg: 'bg-red-400' };
  };
  
  const colors = getColor(score);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 10) * circumference;
  
  if (size === 'small') {
    return (
      <div className="flex items-center gap-3">
        <div className="relative w-12 h-12">
          <svg className="w-12 h-12 -rotate-90">
            <circle cx="24" cy="24" r="20" stroke="#334155" strokeWidth="4" fill="none" />
            <circle 
              cx="24" cy="24" r="20" 
              className={colors.ring}
              strokeWidth="4" 
              fill="none"
              strokeDasharray={2 * Math.PI * 20}
              strokeDashoffset={2 * Math.PI * 20 - (score / 10) * 2 * Math.PI * 20}
              strokeLinecap="round"
            />
          </svg>
          <div className={`absolute inset-0 flex items-center justify-center ${colors.text} font-bold text-sm`}>
            {score}
          </div>
        </div>
        {label && <span className="text-white font-medium">{label}</span>}
      </div>
    );
  }
  
  return (
    <div className="relative w-36 h-36">
      <svg className="w-36 h-36 -rotate-90">
        <circle cx="72" cy="72" r="45" stroke="#334155" strokeWidth="8" fill="none" />
        <circle 
          cx="72" cy="72" r="45" 
          className={colors.ring}
          strokeWidth="8" 
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-4xl font-bold ${colors.text}`}>{score}</span>
        <span className="text-slate-400 text-sm">/10</span>
      </div>
    </div>
  );
}

// Individual Slide Components
function TitleSlide({ name }) {
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[500px] text-center">
      <h1 className="text-6xl font-bold text-white mb-2">LINKEDIN <span className="text-amber-400">AUDIT</span></h1>
      <h2 className="text-3xl text-amber-400 font-semibold tracking-wide">{name.toUpperCase()}</h2>
      <div className="mt-16">
        <div className="text-amber-400 font-bold text-xl tracking-widest">HOWL</div>
        <div className="text-amber-400/70 text-xs tracking-wider">Marketing & Business Development</div>
      </div>
    </div>
  );
}

function GradeSlide({ grades, name }) {
  const overallScore = grades?.overall?.score || 0;
  
  const getVerdict = (score) => {
    if (score >= 8) return { text: 'Excellent', color: 'text-green-400' };
    if (score >= 6) return { text: 'Good, needs polish', color: 'text-yellow-400' };
    if (score >= 4) return { text: 'Needs improvement', color: 'text-orange-400' };
    return { text: 'Requires significant work', color: 'text-red-400' };
  };
  
  const verdict = getVerdict(overallScore);
  
  return (
    <div className="h-full">
      <h1 className="text-4xl font-bold text-amber-400 mb-2">PROFILE</h1>
      <h1 className="text-4xl font-bold text-amber-400 mb-8">SCORECARD</h1>
      
      <div className="flex gap-12">
        {/* Overall Score */}
        <div className="flex flex-col items-center">
          <ScoreCircle score={overallScore} />
          <p className="text-white font-bold mt-4 text-lg">Overall Score</p>
          <p className={`${verdict.color} font-semibold`}>{verdict.text}</p>
          <p className="text-slate-400 text-sm mt-2 max-w-xs text-center">
            {grades?.overall?.explanation}
          </p>
        </div>
        
        {/* Individual Scores */}
        <div className="flex-1 space-y-4">
          <div className="bg-slate-800/50 rounded-lg p-4">
            <div className="flex justify-between items-start">
              <ScoreCircle score={grades?.headline?.score || 0} size="small" label="Headline" />
            </div>
            <p className="text-slate-400 text-sm mt-2 ml-15">{grades?.headline?.explanation}</p>
          </div>
          
          <div className="bg-slate-800/50 rounded-lg p-4">
            <div className="flex justify-between items-start">
              <ScoreCircle score={grades?.banner?.score || 0} size="small" label="Banner" />
            </div>
            <p className="text-slate-400 text-sm mt-2">{grades?.banner?.explanation}</p>
          </div>
          
          <div className="bg-slate-800/50 rounded-lg p-4">
            <div className="flex justify-between items-start">
              <ScoreCircle score={grades?.bio?.score || 0} size="small" label="About/Bio" />
            </div>
            <p className="text-slate-400 text-sm mt-2">{grades?.bio?.explanation}</p>
          </div>
          
          <div className="bg-slate-800/50 rounded-lg p-4">
            <div className="flex justify-between items-start">
              <ScoreCircle score={grades?.proofPoints?.score || 0} size="small" label="Proof Points" />
            </div>
            <p className="text-slate-400 text-sm mt-2">{grades?.proofPoints?.explanation}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnalysisSlide({ analysis }) {
  return (
    <div className="h-full">
      <h1 className="text-4xl font-bold text-amber-400 mb-8">PROFILE ANALYSIS</h1>
      
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-slate-800/50 rounded-lg p-6">
          <h3 className="text-amber-400 font-bold text-lg mb-2">Target Audience</h3>
          <p className="text-white">{analysis?.targetAudience || 'Not determined'}</p>
        </div>
        
        <div className="bg-slate-800/50 rounded-lg p-6">
          <h3 className="text-amber-400 font-bold text-lg mb-2">Core Service</h3>
          <p className="text-white">{analysis?.coreService || 'Not determined'}</p>
        </div>
        
        <div className="bg-slate-800/50 rounded-lg p-6">
          <h3 className="text-amber-400 font-bold text-lg mb-2">Main Problem You Solve</h3>
          <p className="text-white">{analysis?.mainProblem || 'Not determined'}</p>
        </div>
        
        <div className="bg-slate-800/50 rounded-lg p-6">
          <h3 className="text-amber-400 font-bold text-lg mb-2">Proof Points</h3>
          <ul className="text-white space-y-1">
            {analysis?.proofPoints?.map((point, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-amber-400">•</span>
                <span>{point}</span>
              </li>
            )) || <li>Not determined</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function BannerSlide({ banner, grade }) {
  const needsWork = banner?.needsImprovement !== false && grade?.score < 8;

  return (
    <div className="h-full">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-4xl font-bold text-amber-400 mb-2">BANNER</h1>
          <h1 className="text-4xl font-bold text-amber-400">{needsWork ? 'OPTIMIZATION' : 'ANALYSIS'}</h1>
        </div>
        {grade && <ScoreCircle score={grade.score} size="small" label="Current" />}
      </div>

      <div className="grid grid-cols-2 gap-8">
        <div>
          <p className="text-slate-400 mb-4">Current State:</p>
          <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
            <p className="text-white">{banner?.currentState || 'Not visible'}</p>
          </div>

          {needsWork && banner?.suggestedTagline && (
            <>
              <p className="text-amber-400 font-bold mb-2">Suggested Banner Design:</p>
              <div className="bg-gradient-to-r from-slate-800 via-purple-800 to-slate-800 rounded-lg p-6 border border-purple-500/30">
                <p className="text-2xl font-bold text-white mb-1">{banner.suggestedTagline}</p>
                <p className="text-purple-200 text-sm">{banner.suggestedSubtext}</p>
              </div>
            </>
          )}
        </div>

        <div>
          {needsWork && banner?.recommendations?.length > 0 ? (
            <>
              <p className="text-amber-400 font-bold text-lg mb-4">Recommendations:</p>
              <div className="space-y-3">
                {banner.recommendations.map((rec, i) => (
                  <div key={i} className="flex items-start gap-2 text-white">
                    <span className="text-amber-400 font-bold">+</span>
                    <span>{rec}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6">
              <p className="text-green-400 font-bold text-lg mb-2">Your Banner is Strong</p>
              <p className="text-slate-300">Your current banner effectively communicates your value proposition and maintains professional branding. No major changes needed.</p>
              {grade?.explanation && (
                <p className="text-slate-400 text-sm mt-4 italic">"{grade.explanation}"</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function HeadlineSlide({ headlines, grade }) {
  const needsWork = headlines?.needsImprovement !== false && grade?.score < 8;
  const hasOptions = headlines?.options && headlines.options.length > 0;

  return (
    <div className="h-full">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-4xl font-bold text-amber-400 mb-2">HEADLINE</h1>
          <h1 className="text-4xl font-bold text-amber-400">{needsWork ? 'OPTIMIZATION' : 'ANALYSIS'}</h1>
        </div>
        {grade && <ScoreCircle score={grade.score} size="small" label="Current" />}
      </div>

      <div className="mb-6">
        <p className="text-slate-400 mb-2">Current Headline:</p>
        <div className="bg-slate-800/50 rounded-lg p-4">
          <p className="text-white">{headlines?.current || 'Not available'}</p>
        </div>
      </div>

      {needsWork && hasOptions ? (
        <>
          <p className="text-amber-400 font-bold text-lg mb-4">Recommended Headlines:</p>
          <div className="space-y-4">
            {headlines.options.map((option, i) => {
              // Handle both string and object formats
              const text = typeof option === 'string' ? option : option.text;
              const isPrimary = typeof option === 'object' && option.isPrimary;
              const explanation = typeof option === 'object' && option.explanation;

              if (!text) return null; // Skip empty options

              return (
                <div key={i} className={`rounded-lg p-4 ${isPrimary ? 'bg-slate-800/70 border-2 border-amber-400' : 'bg-slate-800/50'}`}>
                  {isPrimary && (
                    <div className="text-amber-400 text-xs font-bold mb-1">★ PRIMARY RECOMMENDATION</div>
                  )}
                  <p className="text-white">{text}</p>
                  {isPrimary && explanation && (
                    <p className="text-slate-400 text-sm mt-2">{explanation}</p>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-purple-300 text-sm mt-6">
            Formula: [What you do + results] | [Who you help] | [Result you deliver] | Role
          </p>
        </>
      ) : (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6">
          <p className="text-green-400 font-bold text-lg mb-2">Your Headline is Strong</p>
          <p className="text-slate-300 mb-4">Your current headline effectively communicates your value proposition and target audience. It follows 2026 best practices for LinkedIn outreach.</p>
          {grade?.explanation && (
            <p className="text-slate-400 text-sm italic">"{grade.explanation}"</p>
          )}
        </div>
      )}
    </div>
  );
}

function BioSlide({ bio, grade }) {
  const needsWork = bio?.needsImprovement !== false && grade?.score < 8;
  const hasRewrite = bio?.rewritten && bio.rewritten !== null;

  return (
    <div className="h-full">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-4xl font-bold text-amber-400 mb-2">BIO</h1>
          <h1 className="text-4xl font-bold text-amber-400">{needsWork && hasRewrite ? 'OPTIMIZATION' : 'ANALYSIS'}</h1>
        </div>
        {grade && <ScoreCircle score={grade.score} size="small" label="Current" />}
      </div>

      {needsWork && hasRewrite ? (
        <div className="grid grid-cols-5 gap-6">
          <div className="col-span-3">
            <p className="text-amber-400 font-bold text-lg mb-3">Your New About Section:</p>
            <div className="bg-white rounded-lg p-4 text-slate-800 text-sm max-h-72 overflow-y-auto">
              <div className="font-bold mb-2">About</div>
              {bio.rewritten.split('\n').map((paragraph, i) => (
                <p key={i} className="mb-3">{paragraph}</p>
              ))}
            </div>
            <p className="text-green-400 text-sm mt-2">
              ✓ {bio.rewritten.length} characters (Target: 1,300-2,000)
            </p>
          </div>

          <div className="col-span-2">
            <p className="text-amber-400 font-bold text-lg mb-3">Bio Structure:</p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-white">
                <span className="text-green-400">✓</span>
                <span>Who you help (first sentence)</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <span className="text-green-400">✓</span>
                <span>The problem you solve</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <span className="text-green-400">✓</span>
                <span>Your unique approach</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <span className="text-green-400">✓</span>
                <span>Social proof</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <span className="text-green-400">✓</span>
                <span>Clear call to action</span>
              </div>
            </div>

            <div className="mt-6 p-3 bg-amber-400/10 border border-amber-400/30 rounded-lg">
              <p className="text-amber-400 text-xs font-bold">TIP</p>
              <p className="text-slate-300 text-sm">Copy this bio directly to your LinkedIn About section</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-slate-400 mb-2">Current About Section:</p>
            <div className="bg-slate-800/50 rounded-lg p-4 max-h-64 overflow-y-auto">
              <p className="text-white text-sm whitespace-pre-wrap">{bio?.current || 'Not visible'}</p>
            </div>
          </div>
          <div>
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6">
              <p className="text-green-400 font-bold text-lg mb-2">Your Bio is Strong</p>
              <p className="text-slate-300 mb-4">Your current About section effectively communicates your value, includes proof points, and follows 2026 best practices.</p>
              {grade?.explanation && (
                <p className="text-slate-400 text-sm italic">"{grade.explanation}"</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StepsSlide({ steps, qualityCheck }) {
  return (
    <div className="h-full">
      <h1 className="text-4xl font-bold text-amber-400 mb-2">SUGGESTED</h1>
      <h1 className="text-4xl font-bold text-amber-400 mb-8">STEPS</h1>

      <div className="space-y-6">
        {steps?.map((step, i) => (
          <div key={i}>
            <h2 className="text-xl font-bold text-amber-400 mb-1">{i + 1}. {step.title}</h2>
            <p className="text-white">{step.description}</p>
          </div>
        ))}
      </div>

      {qualityCheck && (
        <div className="mt-8 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
          <p className="text-green-400 font-bold mb-2">✓ Quality Check</p>
          <p className="text-slate-300 text-sm italic">"{qualityCheck}"</p>
        </div>
      )}
    </div>
  );
}

function CTASlide({ calendlyUrl, name }) {
  const bookingUrl = calendlyUrl || 'https://calendly.com/your-link';

  return (
    <div className="h-full flex flex-col items-center justify-center text-center">
      <h1 className="text-4xl font-bold text-amber-400 mb-2">READY TO</h1>
      <h1 className="text-4xl font-bold text-amber-400 mb-8">TRANSFORM YOUR PROFILE?</h1>

      <div className="max-w-2xl mx-auto mb-8">
        <p className="text-white text-xl mb-4">
          You've seen the gaps. You've got the roadmap.
        </p>
        <p className="text-slate-300 text-lg">
          Now let's make it happen. Book a free strategy call with our team to review your audit and discuss how we can help you get more replies from your LinkedIn outreach.
        </p>
      </div>

      <a
        href={bookingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 font-bold text-xl rounded-lg hover:from-amber-500 hover:to-amber-600 transition transform hover:scale-105 shadow-lg shadow-amber-400/25"
      >
        Book Your Free Strategy Call
      </a>

      <p className="text-slate-400 mt-4 text-sm">
        No obligation. Just a conversation about your LinkedIn goals.
      </p>

      <div className="mt-12 pt-8 border-t border-slate-700/50">
        <div className="text-amber-400 font-bold text-2xl tracking-widest">HOWL</div>
        <div className="text-slate-400 text-sm mt-1">LinkedIn Optimization Experts</div>
      </div>
    </div>
  );
}

export default AuditDeck;